'use client';

import { Card as HeroCard, Link } from '@heroui/react';
import NextImage from 'next/image';

import { Button } from './Button';
import { HStack, VStack } from './Stack';
import { Text } from './Text';

export interface CardProps {
  variant?: 'default' | 'surface' | 'secondary' | 'tertiary' | 'transparent';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  imageSrc?: string;
  imageAlt?: string;
  title?: string;
  description?: string;
  price?: number | string;
  buttonText?: string;
  linkText?: string;
  onPress?: () => void;
  href?: string;
}

export const Card = ({
  variant = 'default',
  shadow = 'md',
  fullWidth,
  imageSrc,
  imageAlt,
  title,
  description,
  price,
  buttonText,
  linkText,
  onPress,
  href,
}: CardProps) => {
  const heroVariant =
    variant === 'surface' || variant === 'default' ? 'default' : variant;

  const shadowClass = shadow === 'none' ? 'shadow-none' : `shadow-${shadow}`;
  const wrapperClasses = `${fullWidth ? 'w-full' : ''} ${shadowClass}`.trim();

  return (
    <HeroCard variant={heroVariant as any} className={wrapperClasses}>
      {imageSrc && (
        <HeroCard.Content className="p-0 overflow-hidden">
          <NextImage
            src={imageSrc}
            alt={imageAlt || title || 'Card image'}
            width={600}
            height={400}
            className="w-full object-cover h-[240px] hover:scale-110 transition-transform duration-700"
          />
        </HeroCard.Content>
      )}
      {(title || description || price || buttonText) && (
        <HeroCard.Footer className="p-4 flex-col items-start gap-4">
          {title && (
            <VStack>
              <HeroCard.Title className="font-bold text-lg m-0">
                {title}
              </HeroCard.Title>
              {price && (
                <Text color="muted" size="md">
                  {price} zł
                </Text>
              )}
            </VStack>
          )}
          {description && (
            <div className="min-h-[3rem] w-full text-left">
              <HeroCard.Description className="text-default-500 text-sm m-0">
                {description}
              </HeroCard.Description>
            </div>
          )}
          <HStack fullWidth align="center" justify="between">
            {buttonText && onPress && (
              <Button size="lg" variant="primary" onPress={onPress}>
                {buttonText}
              </Button>
            )}
            {linkText && href && <Link href={href}>{linkText}</Link>}
          </HStack>
        </HeroCard.Footer>
      )}
    </HeroCard>
  );
};
